/**
 * QueryBuilderPanel — a one-way Visual Query Builder (visual → SQL).
 *
 * Pick a connection + database, add tables to a pan/zoom canvas (reusing the ER
 * diagram's card/edge/force-layout patterns), tick the columns you want, and the
 * builder assembles a SELECT live: FK relationships become JOINs automatically,
 * a nested AND/OR filter builder becomes the WHERE, per-column aggregate/sort/
 * group controls drive GROUP BY / ORDER BY. The generated SQL (web/src/lib/
 * qbsql.js — pure + node-tested) can be copied, run inline, or opened in a real
 * query tab for hand-editing. Round-trip is BOUNDED: "Load SQL" parses a query
 * back into the model ONLY when it fits the builder's own subset (qbparse.js) and
 * otherwise declines with a reason — it never half-parses and changes your SQL.
 */
import { createSignal, createMemo, createResource, createEffect, on, onCleanup, For, Show } from 'solid-js';
import { createStore, reconcile, produce } from 'solid-js/store';
import { api } from '../lib/api.js';
import { fetchERGraph } from '../lib/erdiagram.js';
import { layoutNodes } from '../lib/erlayout.js';
import { buildSQL, nextAlias, AGGREGATES, OPERATORS } from '../lib/qbsql.js';
import { parseSelect } from '../lib/qbparse.js';
import { formatSql } from '../lib/sqlformat.js';
import { createQuerySession, executeQuery, closeQuerySession } from '../lib/querysession.js';
import Modal from '../components/Modal.jsx';
import { toast } from '../lib/toast.js';

const HEADER_H = 30;
const ROW_H = 22;
const CARD_W = 224;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const shortType = (t) => (t || '').replace(/\s+unsigned/i, ' u').replace(/\s+zerofill/i, '');

// Walk a filter tree to the group addressed by `path` (array of item indices).
const groupAt = (root, path) => {
  let g = root;
  for (const idx of path) g = g.items[idx];
  return g;
};

export default function QueryBuilderPanel(props) {
  // ── connection + database selection (mirrors ERDiagramPanel) ────────────────
  const [conns] = createResource(async () => {
    try { return (await api.get('/api/connections')).connections || []; } catch { return []; }
  });
  const [connId, setConnId] = createSignal(props.connectionId ? String(props.connectionId) : '');
  const [dbList, setDbList] = createSignal(null);
  const [database, setDatabase] = createSignal(props.database || '');

  const loadDbs = async (id) => {
    if (!id) { setDbList(null); return; }
    try {
      const r = await api.get(`/api/connections/${id}/databases`);
      setDbList((r.databases || []).map((d) => d.name));
    } catch { setDbList([]); }
  };
  createEffect(() => { if (connId()) loadDbs(connId()); });
  const connObj = () => (conns() || []).find((c) => String(c.id) === String(connId())) || null;

  // ── schema graph (tables + columns + FK edges) ──────────────────────────────
  const graphKey = () => (connId() && database() ? { id: connId(), db: database() } : null);
  const [graph] = createResource(graphKey, (k) => fetchERGraph(k.id, k.db));
  const allTables = () => graph()?.tables || [];
  const allEdges = () => graph()?.edges || [];
  const tableByName = createMemo(() => {
    const m = new Map();
    for (const t of allTables()) m.set(t.name, t);
    return m;
  });
  const colsOf = (name) => tableByName().get(name)?.columns || [];

  // ── the query model (the single source of truth fed to buildSQL) ────────────
  const [model, setModel] = createStore({
    tables: [], joins: [], columns: [],
    filters: { op: 'AND', items: [] },
    distinct: false, limit: null,
  });

  // ── canvas view state ───────────────────────────────────────────────────────
  const [pos, setPos] = createStore({});      // table name -> {x,y}
  const [zoom, setZoom] = createSignal(1);
  const [panX, setPanX] = createSignal(20);
  const [panY, setPanY] = createSignal(20);
  let viewportEl;

  const aliasOf = (name) => model.tables.find((t) => t.name === name)?.alias || name;
  const onCanvas = (name) => model.tables.some((t) => t.name === name);
  const cardH = (name) => HEADER_H + colsOf(name).length * ROW_H + 6;

  // Reset everything when the schema (connection/database) changes.
  createEffect(on(graph, () => {
    setModel(reconcile({ tables: [], joins: [], columns: [], filters: { op: 'AND', items: [] }, distinct: false, limit: null }));
    setPos(reconcile({}));
    setRunResult(null);
  }));

  // ── adding / removing tables ────────────────────────────────────────────────
  const addTable = (name) => {
    if (!name || onCanvas(name)) return;
    const alias = nextAlias(model.tables);
    setModel('tables', (t) => [...t, { name, alias }]);
    // Place at a non-overlapping cascade spot; Auto-layout tidies later.
    const n = model.tables.length - 1;
    if (!pos[name]) setPos(name, { x: 30 + (n % 4) * (CARD_W + 26), y: 24 + Math.floor(n / 4) * 240 });
    // Auto-create INNER joins from FK edges between tables now both on the canvas.
    for (const e of allEdges()) {
      const pair = [e.fromTable, e.toTable];
      if (!pair.includes(name)) continue;
      const other = e.fromTable === name ? e.toTable : e.fromTable;
      if (!onCanvas(other)) continue;
      addJoinIfNew({
        left: { table: e.fromTable, col: e.fromCols[0] },
        right: { table: e.toTable, col: e.toCols[0] },
        type: 'INNER',
      });
    }
    queueMicrotask(fit);
  };

  const removeTable = (name) => {
    setModel(produce((m) => {
      m.tables = m.tables.filter((t) => t.name !== name);
      m.joins = m.joins.filter((j) => j.left.table !== name && j.right.table !== name);
      m.columns = m.columns.filter((c) => c.table !== name);
      pruneFilterTable(m.filters, name);
    }));
    setPos(produce((p) => { delete p[name]; }));
  };

  // Drop filter conditions that reference a removed table (keeps WHERE valid).
  const pruneFilterTable = (group, name) => {
    group.items = group.items.filter((it) => {
      if (it.items) { pruneFilterTable(it, name); return true; }
      return it.table !== name;
    });
  };

  // ── columns (checkbox on a card) ────────────────────────────────────────────
  const isPicked = (table, column) => model.columns.some((c) => c.table === table && c.column === column);
  const toggleColumn = (table, column) => {
    if (isPicked(table, column)) {
      setModel('columns', (cs) => cs.filter((c) => !(c.table === table && c.column === column)));
    } else {
      setModel('columns', (cs) => [...cs, { table, column, alias: '', agg: '', show: true, sort: '', group: false }]);
    }
  };
  const colIndex = (table, column) => model.columns.findIndex((c) => c.table === table && c.column === column);
  const setCol = (i, key, val) => setModel('columns', i, key, val);
  const removeCol = (i) => setModel('columns', (cs) => cs.filter((_, k) => k !== i));
  const moveCol = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= model.columns.length) return;
    setModel('columns', produce((cs) => { const tmp = cs[i]; cs[i] = cs[j]; cs[j] = tmp; }));
  };
  const cycleSort = (i) => {
    const cur = model.columns[i].sort;
    setCol(i, 'sort', cur === '' ? 'ASC' : cur === 'ASC' ? 'DESC' : '');
  };

  // ── joins ───────────────────────────────────────────────────────────────────
  const sameJoin = (a, b) =>
    (a.left.table === b.left.table && a.left.col === b.left.col && a.right.table === b.right.table && a.right.col === b.right.col) ||
    (a.left.table === b.right.table && a.left.col === b.right.col && a.right.table === b.left.table && a.right.col === b.left.col);
  const addJoinIfNew = (j) => { if (!model.joins.some((x) => sameJoin(x, j))) setModel('joins', (js) => [...js, j]); };
  const removeJoin = (i) => setModel('joins', (js) => js.filter((_, k) => k !== i));
  const setJoinType = (i, type) => setModel('joins', i, 'type', type);

  // Manual-join form.
  const [mjLeft, setMjLeft] = createSignal('');   // "table::col"
  const [mjRight, setMjRight] = createSignal('');
  const [mjType, setMjType] = createSignal('INNER');
  const addManualJoin = () => {
    const [lt, lc] = (mjLeft() || '').split('::');
    const [rt, rc] = (mjRight() || '').split('::');
    if (!lt || !lc || !rt || !rc || lt === rt) { toast.error('Pick two columns from different tables'); return; }
    addJoinIfNew({ left: { table: lt, col: lc }, right: { table: rt, col: rc }, type: mjType() });
    setMjLeft(''); setMjRight('');
  };

  // All (table, column) pairs currently on the canvas — for selects.
  const allColOptions = createMemo(() => {
    const out = [];
    for (const t of model.tables) for (const c of colsOf(t.name)) out.push({ table: t.name, column: c.name, key: `${t.name}::${c.name}`, label: `${t.alias}.${c.name}` });
    return out;
  });

  // ── filter builder mutations (produce on nested tree) ───────────────────────
  const addCond = (path) => setModel('filters', produce((f) => { groupAt(f, path).items.push({ table: '', column: '', op: '=', value: '', value2: '' }); }));
  const addSubGroup = (path) => setModel('filters', produce((f) => { groupAt(f, path).items.push({ op: 'AND', items: [] }); }));
  const removeItem = (path, idx) => setModel('filters', produce((f) => { groupAt(f, path).items.splice(idx, 1); }));
  const setGroupOp = (path, op) => setModel('filters', produce((f) => { groupAt(f, path).op = op; }));
  const setCondField = (path, idx, key, val) => setModel('filters', produce((f) => {
    const it = groupAt(f, path).items[idx];
    if (key === 'colkey') { const [t, c] = String(val).split('::'); it.table = t || ''; it.column = c || ''; }
    else it[key] = val;
  }));

  // ── live SQL (pure generator → pretty-print) ────────────────────────────────
  const generated = createMemo(() => buildSQL(model));
  const prettySql = createMemo(() => {
    const { sql } = generated();
    if (!sql) return '';
    try { return formatSql(sql); } catch { return sql; }
  });

  // ── layout / fit / pan / zoom (mirrors ERDiagramPanel) ──────────────────────
  const bounds = createMemo(() => {
    let maxX = 0, maxY = 0;
    for (const t of model.tables) {
      const p = pos[t.name]; if (!p) continue;
      maxX = Math.max(maxX, p.x + CARD_W); maxY = Math.max(maxY, p.y + cardH(t.name));
    }
    return { w: maxX + 60, h: maxY + 60 };
  });
  const fit = () => {
    if (!viewportEl) return;
    const b = bounds();
    const vw = viewportEl.clientWidth, vh = viewportEl.clientHeight;
    if (b.w <= 60 || vw === 0) return;
    const z = clamp(Math.min(vw / b.w, vh / b.h, 1), 0.3, 1);
    setZoom(z); setPanX((vw - b.w * z) / 2); setPanY(Math.max(12, (vh - b.h * z) / 2));
  };
  const autoLayout = () => {
    if (model.tables.length === 0) return;
    const nodes = model.tables.map((t) => ({ id: t.name, w: CARD_W, h: cardH(t.name) }));
    const edges = model.joins.map((j) => ({ source: j.left.table, target: j.right.table }));
    setPos(reconcile(layoutNodes(nodes, edges)));
    queueMicrotask(fit);
  };
  const onWheel = (e) => {
    e.preventDefault();
    const rect = viewportEl.getBoundingClientRect();
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
    const wx = (sx - panX()) / zoom(), wy = (sy - panY()) / zoom();
    const nz = clamp(zoom() * (e.deltaY < 0 ? 1.12 : 1 / 1.12), 0.3, 2.2);
    setPanX(sx - wx * nz); setPanY(sy - wy * nz); setZoom(nz);
  };
  const zoomBy = (f) => {
    const vw = viewportEl.clientWidth / 2, vh = viewportEl.clientHeight / 2;
    const wx = (vw - panX()) / zoom(), wy = (vh - panY()) / zoom();
    const nz = clamp(zoom() * f, 0.3, 2.2);
    setPanX(vw - wx * nz); setPanY(vh - wy * nz); setZoom(nz);
  };
  let panStart = null;
  const onViewportPointerDown = (e) => {
    if (e.target.closest('.qb-card')) return;
    panStart = { x: e.clientX, y: e.clientY, px: panX(), py: panY() };
    viewportEl.setPointerCapture?.(e.pointerId);
  };
  const onViewportPointerMove = (e) => {
    if (!panStart) return;
    setPanX(panStart.px + (e.clientX - panStart.x));
    setPanY(panStart.py + (e.clientY - panStart.y));
  };
  const endPan = () => { panStart = null; };

  // ── card drag ────────────────────────────────────────────────────────────────
  let drag = null;
  const onCardPointerDown = (e, name) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const p = pos[name] || { x: 0, y: 0 };
    drag = { name, sx: e.clientX, sy: e.clientY, ox: p.x, oy: p.y };
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', onDragUp);
  };
  const onDragMove = (e) => {
    if (!drag) return;
    const dx = (e.clientX - drag.sx) / zoom(), dy = (e.clientY - drag.sy) / zoom();
    setPos(drag.name, { x: Math.round(drag.ox + dx), y: Math.round(drag.oy + dy) });
  };
  const onDragUp = () => {
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', onDragUp);
    drag = null;
  };
  onCleanup(() => { window.removeEventListener('pointermove', onDragMove); window.removeEventListener('pointerup', onDragUp); });

  // ── join edge geometry (center-to-center bezier; chip at midpoint) ───────────
  const edgeGeom = (j) => {
    const sp = pos[j.left.table], tp = pos[j.right.table];
    if (!sp || !tp) return null;
    const sh = cardH(j.left.table), th = cardH(j.right.table);
    const scx = sp.x + CARD_W / 2, tcx = tp.x + CARD_W / 2;
    const sRight = tcx >= scx;
    const sx = sp.x + (sRight ? CARD_W : 0), sy = sp.y + sh / 2;
    const tx = tp.x + (sRight ? 0 : CARD_W), ty = tp.y + th / 2;
    const d = Math.max(40, Math.abs(tx - sx) * 0.45);
    const c1x = sx + (sRight ? d : -d), c2x = tx + (sRight ? -d : d);
    return { path: `M ${sx} ${sy} C ${c1x} ${sy}, ${c2x} ${ty}, ${tx} ${ty}`, mx: (sx + tx) / 2, my: (sy + ty) / 2 };
  };

  // ── actions: copy / run / open in query tab ─────────────────────────────────
  const [runResult, setRunResult] = createSignal(null);  // {columns,rows,ms,error,affected}
  const [running, setRunning] = createSignal(false);

  const copySql = async () => {
    const sql = prettySql();
    if (!sql) return;
    try { await navigator.clipboard.writeText(sql); toast.success('SQL copied'); }
    catch { toast.error('Copy failed'); }
  };

  const runInline = async () => {
    const { sql } = generated();
    if (!sql || !connId() || !database()) { toast.error('Pick a connection, database and at least one table'); return; }
    setRunning(true); setRunResult(null);
    let sid = null;
    try {
      const s = await createQuerySession(connId(), database());
      sid = s.sessionId || s.id;
      const t0 = performance.now();
      const r = await executeQuery(sid, sql, true);
      const ms = Math.round(performance.now() - t0);
      const rs = (r.resultSets || [])[0];
      if (rs) setRunResult({ columns: (rs.columns || []).map((c) => c.name), rows: rs.rows || [], ms });
      else setRunResult({ columns: [], rows: [], ms, affected: r.rowsAffected || 0 });
    } catch (err) {
      setRunResult({ error: String(err?.message || err) });
    } finally {
      if (sid) closeQuerySession(sid);
      setRunning(false);
    }
  };

  const openInTab = () => {
    const { sql } = generated();
    const c = connObj();
    if (!sql || !c) { toast.error('Pick a connection, database and at least one table'); return; }
    props.onOpenQuery?.(c, database(), prettySql());
  };

  // ── bounded round-trip: load SQL back into the builder (qbparse) ─────────────
  const [loadOpen, setLoadOpen] = createSignal(false);
  const [loadText, setLoadText] = createSignal('');
  const [loadErr, setLoadErr] = createSignal('');
  const doLoad = () => {
    const r = parseSelect(loadText());
    if (!r.ok) { setLoadErr(r.reason); return; }
    setModel(reconcile(r.model));
    setPos(reconcile({}));
    setRunResult(null);
    setLoadErr('');
    setLoadOpen(false);
    queueMicrotask(autoLayout);
    const unknown = r.model.tables.filter((t) => !tableByName().has(t.name)).map((t) => t.name);
    if (unknown.length) toast.info(`Loaded. Tables not in this database (no columns shown): ${unknown.join(', ')}`);
    else toast.success('Loaded into the builder');
  };

  // ── table picker ────────────────────────────────────────────────────────────
  const [pickSearch, setPickSearch] = createSignal('');
  const [pickOpen, setPickOpen] = createSignal(false);
  const pickable = createMemo(() => {
    const q = pickSearch().trim().toLowerCase();
    return allTables().filter((t) => !onCanvas(t.name) && (!q || t.name.toLowerCase().includes(q)));
  });

  return (
    <div class="qb-panel">
      {/* Toolbar */}
      <div class="qb-toolbar">
        <select class="input qb-sel" value={connId()} onChange={(e) => { setConnId(e.currentTarget.value); setDatabase(''); }}>
          <option value="">— connection —</option>
          <For each={conns() || []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
        </select>
        <select class="input qb-sel" value={database()} onChange={(e) => setDatabase(e.currentTarget.value)} disabled={!dbList()}>
          <option value="">— database —</option>
          <For each={dbList() || []}>{(n) => <option value={n}>{n}</option>}</For>
        </select>

        <Show when={graph()}>
          <span class="qb-divider" />
          <div class="qb-picker">
            <button class="btn btn-sm btn-ghost" onClick={() => setPickOpen(!pickOpen())} disabled={allTables().length === 0}>＋ Add table ▾</button>
            <Show when={pickOpen()}>
              <div class="qb-picker-pop" onMouseLeave={() => setPickOpen(false)}>
                <input class="input qb-picker-search" placeholder="Find table…" autofocus
                       value={pickSearch()} onInput={(e) => setPickSearch(e.currentTarget.value)} />
                <div class="qb-picker-list">
                  <For each={pickable()} fallback={<div class="qb-picker-empty muted">No tables</div>}>
                    {(t) => <button class="qb-picker-item" onClick={() => { addTable(t.name); setPickOpen(false); setPickSearch(''); }}>{t.name}</button>}
                  </For>
                </div>
              </div>
            </Show>
          </div>
          <button class="btn btn-sm btn-ghost" onClick={autoLayout} title="Re-run auto-layout" disabled={model.tables.length === 0}>✦ Auto-layout</button>
          <button class="btn btn-sm btn-ghost" onClick={fit} title="Fit to view" disabled={model.tables.length === 0}>⤢ Fit</button>
          <div class="qb-zoom">
            <button class="btn btn-sm btn-ghost" onClick={() => zoomBy(1 / 1.2)}>−</button>
            <span class="qb-zoom-val">{Math.round(zoom() * 100)}%</span>
            <button class="btn btn-sm btn-ghost" onClick={() => zoomBy(1.2)}>+</button>
          </div>
          <span class="spacer" />
          <button class="btn btn-sm btn-ghost" onClick={() => { setLoadText(''); setLoadErr(''); setLoadOpen(true); }} title="Load an existing SELECT into the builder (only simple queries the builder can represent)">⤵ Load SQL</button>
          <button class="btn btn-sm" onClick={copySql} disabled={!generated().sql}>⧉ Copy</button>
          <button class="btn btn-sm" onClick={openInTab} disabled={!generated().sql}>↗ Open in query tab</button>
          <button class="btn btn-sm btn-primary" onClick={runInline} disabled={!generated().sql || running()}>{running() ? 'Running…' : '▶ Run'}</button>
        </Show>
      </div>

      <Show when={!graphKey()}>
        <div class="qb-empty">Pick a connection and database, then add tables to build a query.</div>
      </Show>
      <Show when={graph.loading}><div class="qb-empty">Loading schema…</div></Show>
      <Show when={graph.error}><div class="qb-empty qb-err">Couldn't load schema: {String(graph.error?.message || graph.error)}</div></Show>

      <Show when={graph() && !graph.loading}>
        <div class="qb-body">
          {/* Canvas */}
          <div class="qb-viewport" ref={viewportEl}
               onWheel={onWheel} onPointerDown={onViewportPointerDown} onPointerMove={onViewportPointerMove}
               onPointerUp={endPan} onPointerLeave={endPan}>
            <Show when={model.tables.length === 0}>
              <div class="qb-empty qb-empty-canvas">Add a table to start. The first table is the FROM; foreign keys become JOINs automatically.</div>
            </Show>
            <div class="qb-world" style={`transform:translate(${panX()}px,${panY()}px) scale(${zoom()});width:${bounds().w}px;height:${bounds().h}px;`}>
              {/* Join edges */}
              <svg class="qb-edges" width={bounds().w} height={bounds().h}>
                <For each={model.joins}>
                  {(j) => { const g = () => edgeGeom(j); return <Show when={g()}><path d={g().path} class="qb-edge" /></Show>; }}
                </For>
              </svg>
              {/* Join chips (HTML, in world coords) */}
              <For each={model.joins}>
                {(j, i) => {
                  const g = () => edgeGeom(j);
                  return (
                    <Show when={g()}>
                      <div class="qb-join-chip" style={`left:${g().mx}px;top:${g().my}px;`}>
                        <select class="qb-join-type" value={j.type} onChange={(e) => setJoinType(i(), e.currentTarget.value)}
                                title={`${aliasOf(j.left.table)}.${j.left.col} = ${aliasOf(j.right.table)}.${j.right.col}`}>
                          <option value="INNER">INNER</option>
                          <option value="LEFT">LEFT</option>
                          <option value="RIGHT">RIGHT</option>
                        </select>
                        <button class="qb-join-x" title="Remove join" onClick={() => removeJoin(i())}>×</button>
                      </div>
                    </Show>
                  );
                }}
              </For>
              {/* Table cards */}
              <For each={model.tables}>
                {(t, ti) => {
                  const p = () => pos[t.name] || { x: 0, y: 0 };
                  return (
                    <div class="qb-card" style={`left:${p().x}px;top:${p().y}px;width:${CARD_W}px;`}>
                      <div class="qb-card-head" onPointerDown={(e) => onCardPointerDown(e, t.name)} title="Drag to move">
                        <span class="qb-card-alias">{t.alias}</span>
                        <span class="qb-card-name">{t.name}</span>
                        <Show when={ti() === 0}><span class="qb-card-from" title="FROM root">FROM</span></Show>
                        <button class="qb-card-x" title="Remove table" onPointerDown={(e) => e.stopPropagation()} onClick={() => removeTable(t.name)}>×</button>
                      </div>
                      <div class="qb-card-cols">
                        <For each={colsOf(t.name)}>
                          {(c) => (
                            <label class="qb-col" classList={{ 'qb-col-on': isPicked(t.name, c.name) }}>
                              <input type="checkbox" checked={isPicked(t.name, c.name)} onChange={() => toggleColumn(t.name, c.name)} />
                              <span class="qb-col-key">{c.pk ? '🔑' : c.fk ? '→' : ''}</span>
                              <span class="qb-col-name">{c.name}</span>
                              <span class="qb-col-type">{shortType(c.type)}</span>
                            </label>
                          )}
                        </For>
                      </div>
                    </div>
                  );
                }}
              </For>
            </div>
          </div>

          {/* Config sidebar */}
          <div class="qb-side">
            {/* Selected columns */}
            <div class="qb-section">
              <div class="qb-section-h">Columns <span class="muted">({model.columns.length})</span></div>
              <Show when={model.columns.length === 0}><div class="qb-hint muted">Tick columns on the cards to select them.</div></Show>
              <For each={model.columns}>
                {(c, i) => (
                  <div class="qb-colrow">
                    <div class="qb-colrow-top">
                      <span class="qb-colrow-name" title={`${aliasOf(c.table)}.${c.column}`}>{aliasOf(c.table)}.{c.column}</span>
                      <span class="qb-colrow-tools">
                        <button class="qb-mini" title="Move up" onClick={() => moveCol(i(), -1)}>↑</button>
                        <button class="qb-mini" title="Move down" onClick={() => moveCol(i(), 1)}>↓</button>
                        <button class="qb-mini" title="Remove" onClick={() => removeCol(i())}>×</button>
                      </span>
                    </div>
                    <div class="qb-colrow-ctl">
                      <select class="input qb-mini-sel" value={c.agg} onChange={(e) => setCol(i(), 'agg', e.currentTarget.value)} title="Aggregate">
                        <For each={AGGREGATES}>{(a) => <option value={a.v}>{a.label}</option>}</For>
                      </select>
                      <input class="input qb-mini-in" placeholder="alias" value={c.alias} onInput={(e) => setCol(i(), 'alias', e.currentTarget.value)} />
                      <button class="qb-mini qb-sortbtn" classList={{ 'qb-on': !!c.sort }} title="Sort" onClick={() => cycleSort(i())}>
                        {c.sort === 'ASC' ? '↑ ASC' : c.sort === 'DESC' ? '↓ DESC' : 'sort'}
                      </button>
                      <label class="qb-grp" title="Add to GROUP BY"><input type="checkbox" checked={c.group} onChange={(e) => setCol(i(), 'group', e.currentTarget.checked)} /> grp</label>
                    </div>
                  </div>
                )}
              </For>
            </div>

            {/* Joins */}
            <div class="qb-section">
              <div class="qb-section-h">Joins <span class="muted">({model.joins.length})</span></div>
              <Show when={model.tables.length >= 2}>
                <div class="qb-mjoin">
                  <select class="input qb-mini-sel" value={mjLeft()} onChange={(e) => setMjLeft(e.currentTarget.value)}>
                    <option value="">left…</option>
                    <For each={allColOptions()}>{(o) => <option value={o.key}>{o.label}</option>}</For>
                  </select>
                  <select class="input qb-mini-sel qb-mjoin-type" value={mjType()} onChange={(e) => setMjType(e.currentTarget.value)}>
                    <option value="INNER">=</option><option value="LEFT">⟕</option><option value="RIGHT">⟖</option>
                  </select>
                  <select class="input qb-mini-sel" value={mjRight()} onChange={(e) => setMjRight(e.currentTarget.value)}>
                    <option value="">right…</option>
                    <For each={allColOptions()}>{(o) => <option value={o.key}>{o.label}</option>}</For>
                  </select>
                  <button class="btn btn-sm btn-ghost" onClick={addManualJoin}>＋</button>
                </div>
              </Show>
              <Show when={model.joins.length === 0}><div class="qb-hint muted">FK joins appear automatically. Add manual joins above.</div></Show>
            </div>

            {/* Filters */}
            <div class="qb-section">
              <div class="qb-section-h">Filters (WHERE)</div>
              <FilterGroup group={model.filters} path={[]} cols={allColOptions()}
                           addCond={addCond} addSubGroup={addSubGroup} removeItem={removeItem} setGroupOp={setGroupOp} setCondField={setCondField} />
            </div>

            {/* Options */}
            <div class="qb-section">
              <div class="qb-section-h">Options</div>
              <label class="qb-opt"><input type="checkbox" checked={model.distinct} onChange={(e) => setModel('distinct', e.currentTarget.checked)} /> DISTINCT</label>
              <label class="qb-opt">LIMIT <input class="input qb-mini-in" type="number" min="0" placeholder="none" value={model.limit ?? ''} onInput={(e) => setModel('limit', e.currentTarget.value === '' ? null : Number(e.currentTarget.value))} /></label>
            </div>
          </div>
        </div>

        {/* Footer: SQL preview + warnings + inline results */}
        <div class="qb-foot">
          <div class="qb-foot-sql">
            <div class="qb-foot-h">SQL
              <Show when={generated().warnings.length}>
                <span class="qb-warn" title={generated().warnings.join('\n')}>⚠ {generated().warnings.length}</span>
              </Show>
            </div>
            <pre class="qb-sql">{prettySql() || '-- add a table to begin'}</pre>
          </div>
          <Show when={runResult()}>
            <div class="qb-foot-res">
              <Show when={runResult().error}><div class="qb-res-err">{runResult().error}</div></Show>
              <Show when={!runResult().error && runResult().columns.length === 0}>
                <div class="qb-res-ok">OK — {runResult().affected ?? 0} row(s) affected · {runResult().ms}ms</div>
              </Show>
              <Show when={!runResult().error && runResult().columns.length > 0}>
                <div class="qb-res-meta muted">{runResult().rows.length} row(s) · {runResult().ms}ms</div>
                <div class="qb-res-grid">
                  <table>
                    <thead><tr><For each={runResult().columns}>{(c) => <th>{c}</th>}</For></tr></thead>
                    <tbody>
                      <For each={runResult().rows.slice(0, 200)}>
                        {(row) => <tr><For each={row}>{(cell) => <td>{cell === null ? <span class="qb-null">NULL</span> : String(cell)}</td>}</For></tr>}
                      </For>
                    </tbody>
                  </table>
                </div>
              </Show>
            </div>
          </Show>
        </div>
      </Show>

      {/* Bounded round-trip: paste a SELECT to load it into the builder. */}
      <Modal open={loadOpen()} onClose={() => setLoadOpen(false)} title="Load SQL into the builder" cardClass="qb-load-card">
        <p class="muted qb-load-hint">Paste a <strong>single SELECT</strong>. The builder loads queries it can fully represent (simple joins, comparison filters, aggregates, GROUP/ORDER BY) and declines anything more complex rather than risk changing it.</p>
        <textarea class="input qb-load-ta" rows="7" spellcheck={false} placeholder="SELECT … FROM …"
                  value={loadText()} onInput={(e) => { setLoadText(e.currentTarget.value); setLoadErr(''); }} />
        <Show when={loadErr()}><div class="qb-load-err">⚠ {loadErr()}</div></Show>
        <div class="qb-load-foot">
          <button class="btn btn-ghost" onClick={() => setLoadOpen(false)}>Cancel</button>
          <button class="btn btn-primary" onClick={doLoad} disabled={!loadText().trim()}>Load</button>
        </div>
      </Modal>
    </div>
  );
}

// ── recursive filter-group component ──────────────────────────────────────────
function FilterGroup(props) {
  const g = () => props.group;
  return (
    <div class="qb-fgroup">
      <div class="qb-fgroup-h">
        <div class="qb-andor">
          <button classList={{ 'qb-on': g().op === 'AND' }} onClick={() => props.setGroupOp(props.path, 'AND')}>AND</button>
          <button classList={{ 'qb-on': g().op === 'OR' }} onClick={() => props.setGroupOp(props.path, 'OR')}>OR</button>
        </div>
        <span class="qb-fgroup-tools">
          <button class="qb-mini" onClick={() => props.addCond(props.path)} title="Add condition">＋ cond</button>
          <button class="qb-mini" onClick={() => props.addSubGroup(props.path)} title="Add nested group">＋ ( )</button>
        </span>
      </div>
      <div class="qb-fitems">
        <For each={g().items}>
          {(it, i) => (
            <Show when={it.items} fallback={
              <div class="qb-cond">
                <select class="input qb-mini-sel" value={it.table && it.column ? `${it.table}::${it.column}` : ''}
                        onChange={(e) => props.setCondField(props.path, i(), 'colkey', e.currentTarget.value)}>
                  <option value="">column…</option>
                  <For each={props.cols}>{(o) => <option value={o.key}>{o.label}</option>}</For>
                </select>
                <select class="input qb-mini-sel qb-op" value={it.op} onChange={(e) => props.setCondField(props.path, i(), 'op', e.currentTarget.value)}>
                  <For each={OPERATORS}>{(o) => <option value={o.v}>{o.label}</option>}</For>
                </select>
                {(() => {
                  const ar = () => (OPERATORS.find((o) => o.v === it.op) || { arity: 1 }).arity;
                  return (
                    <Show when={ar() !== 0}>
                      <Show when={ar() === 'list'} fallback={
                        <>
                          <input class="input qb-mini-in" placeholder="value" value={it.value} onInput={(e) => props.setCondField(props.path, i(), 'value', e.currentTarget.value)} />
                          <Show when={ar() === 2}>
                            <span class="qb-and-lbl">and</span>
                            <input class="input qb-mini-in" placeholder="value" value={it.value2} onInput={(e) => props.setCondField(props.path, i(), 'value2', e.currentTarget.value)} />
                          </Show>
                        </>
                      }>
                        <input class="input qb-mini-in" placeholder="a, b, c" value={it.value} onInput={(e) => props.setCondField(props.path, i(), 'value', e.currentTarget.value)} />
                      </Show>
                    </Show>
                  );
                })()}
                <button class="qb-mini" title="Remove" onClick={() => props.removeItem(props.path, i())}>×</button>
              </div>
            }>
              <div class="qb-subgroup">
                <button class="qb-mini qb-subgroup-x" title="Remove group" onClick={() => props.removeItem(props.path, i())}>× group</button>
                <FilterGroup group={it} path={[...props.path, i()]} cols={props.cols}
                             addCond={props.addCond} addSubGroup={props.addSubGroup} removeItem={props.removeItem} setGroupOp={props.setGroupOp} setCondField={props.setCondField} />
              </div>
            </Show>
          )}
        </For>
        <Show when={g().items.length === 0}><div class="qb-hint muted">No filters.</div></Show>
      </div>
    </div>
  );
}
